import { useTranslation } from "react-i18next";
import { WebhookEvent } from "../../services/swagger";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FieldLabel from "../atoms/FieldLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo } from "react";

interface SingleEventCheckboxProps {
  name: string;
  tag: WebhookEvent;
  selectedEvents: WebhookEvent[];
  onToggle: (event: WebhookEvent) => void;
}

interface WebhookEventSelectProps {
  value: WebhookEvent[];
  onChange: (value: WebhookEvent[]) => void;
}

function SingleEventCheckbox(props: SingleEventCheckboxProps) {
  const { selectedEvents } = props;

  const checked = useMemo(() => {
    return selectedEvents.includes(props.tag);
  }, [selectedEvents, props.tag]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      justifyContent="space-between"
    >
      <Stack>
        <Typography fontWeight="bold">{props.name}</Typography>
        <Typography variant="body2" color="textSecondary">
          {props.tag}
        </Typography>
      </Stack>
      <Checkbox checked={checked} onChange={() => props.onToggle(props.tag)} />
    </Stack>
  );
}

export default function WebhookEventSelect(props: WebhookEventSelectProps) {
  const { value, onChange } = props;
  const { t } = useTranslation("WebhookEventSelect");

  const handleToggleEvent = (event: WebhookEvent) => {
    if (value.includes(event)) {
      onChange(value.filter((e) => e !== event));
    } else {
      onChange([...value, event]);
    }
  };

  return (
    <Stack spacing={1}>
      <Paper sx={{ maxWidth: 700 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("TicketEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("Ticket.TicketCreated")}
                tag={WebhookEvent.TicketCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketUpdated")}
                tag={WebhookEvent.TicketUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketDeleted")}
                tag={WebhookEvent.TicketDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketResponseCreated")}
                tag={WebhookEvent.TicketResponseCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketResponseUpdated")}
                tag={WebhookEvent.TicketResponseUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketResponseDeleted")}
                tag={WebhookEvent.TicketResponseDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketAssigneeAdded")}
                tag={WebhookEvent.TicketAssigneeAdded}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketAssigneeRemoved")}
                tag={WebhookEvent.TicketAssigneeRemoved}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Ticket.TicketDetailsUpdated")}
                tag={WebhookEvent.TicketDetailsUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("UserEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("User.UserCreated")}
                tag={WebhookEvent.UserCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("User.UserRegistered")}
                tag={WebhookEvent.UserRegistered}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("User.UserUpdated")}
                tag={WebhookEvent.UserUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("User.UserDeleted")}
                tag={WebhookEvent.UserDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("FileEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("File.FileUploaded")}
                tag={WebhookEvent.FileUploaded}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("File.FileDeleted")}
                tag={WebhookEvent.FileDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("File.FileDirectoryCreated")}
                tag={WebhookEvent.FileDirectoryCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("File.FileDirectoryDeleted")}
                tag={WebhookEvent.FileDirectoryDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("TaskEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("Task.TaskCreated")}
                tag={WebhookEvent.TaskCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskUpdated")}
                tag={WebhookEvent.TaskUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskDeleted")}
                tag={WebhookEvent.TaskDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskTimelogCreated")}
                tag={WebhookEvent.TaskTimelogCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskTimelogUpdated")}
                tag={WebhookEvent.TaskTimelogUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskTimelogDeleted")}
                tag={WebhookEvent.TaskTimelogDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskUserAssigned")}
                tag={WebhookEvent.TaskUserAssigned}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskUserUnassigned")}
                tag={WebhookEvent.TaskUserUnassigned}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskProductsAdded")}
                tag={WebhookEvent.TaskProductsAdded}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskProductsDeleted")}
                tag={WebhookEvent.TaskProductsDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskProductsSerialsUpdated")}
                tag={WebhookEvent.TaskProductsSerialsUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Task.TaskStatusUpdated")}
                tag={WebhookEvent.TaskStatusUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("FirewallEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("Firewall.FirewallRecordCreated")}
                tag={WebhookEvent.FirewallRecordCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Firewall.FirewallRecordUpdated")}
                tag={WebhookEvent.FirewallRecordUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Firewall.FirewallRecordDeleted")}
                tag={WebhookEvent.FirewallRecordDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" color="primary.main">
              {t("DepartmentEvents")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack ml={2} spacing={2} divider={<Divider />}>
              <SingleEventCheckbox
                name={t("Department.DepartmentCreated")}
                tag={WebhookEvent.DepartmentCreated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Department.DepartmentUpdated")}
                tag={WebhookEvent.DepartmentUpdated}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
              <SingleEventCheckbox
                name={t("Department.DepartmentDeleted")}
                tag={WebhookEvent.DepartmentDeleted}
                selectedEvents={value}
                onToggle={handleToggleEvent}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Stack>
  );
}
